// src/components/admin/page-sections/NewsletterSection.js
import React, { useState } from 'react';
import { Mail } from 'lucide-react';

const NewsletterSection = ({ content }) => {
  const {
    title = 'Join Our Newsletter',
    subtitle = 'Stay updated with our latest products, promotions, and packaging tips.',
    buttonText = 'Subscribe',
    placeholderText = 'Enter your email',
    successMessage = 'Thank you for subscribing!',
    backgroundColor = '#f3f4f6',
    textColor = '#111827',
    subtitleColor = '#4b5563',
    buttonColor = '#013263',
    buttonTextColor = '#ffffff',
    inputBackgroundColor = '#ffffff',
    inputTextColor = '#1f2937',
    inputBorderColor = '#d1d5db',
    hasBackgroundImage = false,
    backgroundImageUrl = '',
    overlayEnabled = false,
    overlayColor = 'rgba(0,0,0,0.5)',
    paddingTop = '4rem',
    paddingBottom = '4rem',
    marginBottom = '3rem',
    maxWidth = '36rem',
    buttonHoverEffect = true,
    borderRadius = '0.375rem',
    showIcon = true,
    iconColor = '#374151',
    boxShadow = false,
    privacyText = 'We respect your privacy and will never share your information.',
    showPrivacyText = true,
    roundedInput = true,
    showDivider = true,
    dividerColor = '#e5e7eb',
    backgroundGradient = false,
    gradientFrom = '#f9fafb',
    gradientTo = '#f3f4f6'
  } = content || {};
  
  // State for subscription form
  const [subscriptionData, setSubscriptionData] = useState({
    firstName: '',
    email: '',
    interests: '',
    phone: '',
    consent: false
  });
  const [subscriptionStatus, setSubscriptionStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [subscriptionError, setSubscriptionError] = useState('');

  // Handle subscription form input changes
  const handleSubscriptionChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSubscriptionData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle subscription form submission
  const handleSubscribe = async (e) => {
    e.preventDefault();
    
    if (!subscriptionData.email) {
      setSubscriptionError('Email address is required');
      setSubscriptionStatus('error');
      return;
    }
    
    if (!subscriptionData.consent) {
      setSubscriptionError('You must agree to receive marketing emails');
      setSubscriptionStatus('error');
      return;
    }
    
    setSubscriptionStatus('loading');
    
    try {
      const response = await fetch('/api/subscriptions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: subscriptionData.email,
          firstName: subscriptionData.firstName || '',
          interests: subscriptionData.interests || 'all',
          phone: subscriptionData.phone || '', 
          source: 'website_newsletter'
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to subscribe');
      }
      
      setSubscriptionStatus('success');
      setSubscriptionData({
        firstName: '',
        email: '',
        interests: '',
        phone: '', 
        consent: false
      });
      
    } catch (error) {
      console.error('Subscription error:', error);
      setSubscriptionError(error.message || 'Failed to subscribe. Please try again.');
      setSubscriptionStatus('error');
    }
  };
  
  const sectionStyle = {
    paddingTop,
    paddingBottom,
    marginBottom,
    color: textColor,
    position: 'relative'
  };
  
  if (hasBackgroundImage && backgroundImageUrl) {
    sectionStyle.backgroundImage = `url(${backgroundImageUrl})`;
    sectionStyle.backgroundSize = 'cover';
    sectionStyle.backgroundPosition = 'center';
  } else if (backgroundGradient) {
    sectionStyle.background = `linear-gradient(to bottom, ${gradientFrom}, ${gradientTo})`;
  } else {
    sectionStyle.backgroundColor = backgroundColor;
  }
  
  if (boxShadow) {
    sectionStyle.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
  }

  return (
    <div style={sectionStyle}>
      {hasBackgroundImage && overlayEnabled && (
        <div 
          className="absolute inset-0"
          style={{ backgroundColor: overlayColor }}
        ></div>
      )}
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="max-w-3xl mx-auto text-center">
          <div className="transform transition-all duration-700">
            <h2 className="text-3xl font-bold mb-4" style={{ color: textColor }}>{title}</h2>
            <p className="mb-8 text-lg" style={{ color: subtitleColor }}>{subtitle}</p>
            
            <div className="bg-white p-6 rounded-lg shadow-lg">
              {subscriptionStatus === 'success' ? (
                <div className="text-center py-4">
                  <div className="text-green-600 text-5xl mb-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{successMessage}</h3>
                  <p className="text-gray-600">You're now on our list to receive the latest news and updates.</p>
                </div>
              ) : (
                <form onSubmit={handleSubscribe} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="subscribeFirstName" className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                      <input
                        type="text"
                        id="subscribeFirstName"
                        name="firstName"
                        value={subscriptionData.firstName}
                        onChange={handleSubscriptionChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Your first name"
                        style={{
                          backgroundColor: inputBackgroundColor,
                          color: inputTextColor,
                          borderColor: inputBorderColor,
                          borderRadius: roundedInput ? borderRadius : '0'
                        }}
                      />
                    </div>
                    <div>
                      <label htmlFor="subscribePhone" className="block text-sm font-medium text-gray-700 mb-1">Phone Number (Optional)</label>
                      <input
                        type="tel"
                        id="subscribePhone"
                        name="phone"
                        value={subscriptionData.phone || ''}
                        onChange={handleSubscriptionChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Your phone number"
                        style={{
                          backgroundColor: inputBackgroundColor,
                          color: inputTextColor,
                          borderColor: inputBorderColor,
                          borderRadius: roundedInput ? borderRadius : '0'
                        }}
                      />
                    </div>                      
                  </div>
                  <div>
                    <label htmlFor="subscribeEmail" className="block text-sm font-medium text-gray-700 mb-1">Email Address*</label>
                    <div className="relative">
                      {showIcon && (
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" style={{ color: iconColor }}>
                          <Mail className="h-5 w-5" />
                        </div>
                      )}
                      <input
                        type="email"
                        id="subscribeEmail"
                        name="email"
                        value={subscriptionData.email}
                        onChange={handleSubscriptionChange}
                        required
                        className={`w-full ${showIcon ? 'pl-10' : 'pl-4'} pr-4 py-2 border rounded-md`}
                        placeholder={placeholderText}
                        style={{
                          backgroundColor: inputBackgroundColor,
                          color: inputTextColor,
                          borderColor: inputBorderColor,
                          borderRadius: roundedInput ? borderRadius : '0'
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subscribeInterests" className="block text-sm font-medium text-gray-700 mb-1">Interests</label>
                    <select
                      id="subscribeInterests"
                      name="interests"
                      value={subscriptionData.interests}
                      onChange={handleSubscriptionChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      style={{
                        backgroundColor: inputBackgroundColor,
                        color: inputTextColor,
                        borderColor: inputBorderColor,
                        borderRadius: roundedInput ? borderRadius : '0'
                      }}
                    >
                      <option value="">Select your main interest</option>
                      <option value="custom_bags">Custom Bags</option>
                      <option value="custom_boxes">Custom Boxes</option>
                      <option value="retail_packaging">Retail Packaging</option>
                      <option value="eco_friendly">Eco-Friendly Solutions</option>
                      <option value="all">All Products</option>
                    </select>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="subscribeConsent"
                        name="consent"
                        type="checkbox"
                        checked={subscriptionData.consent}
                        onChange={handleSubscriptionChange}
                        required
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </div>
                    <div className="ml-3">
                      <label htmlFor="subscribeConsent" className="text-sm text-gray-600">
                        {privacyText || 'I agree to receive marketing emails and updates. You can unsubscribe at any time.'}
                      </label>
                    </div>
                  </div>
                  
                  {subscriptionStatus === 'error' && (
                    <div className="text-red-600 text-sm p-2 bg-red-50 rounded">
                      {subscriptionError}
                    </div>
                  )}
                  
                  <div className="text-center">
                    <button
                      type="submit"
                      disabled={subscriptionStatus === 'loading'}
                      className={`px-6 py-3 font-semibold rounded-md transition-colors duration-300 inline-flex items-center ${buttonHoverEffect ? 'hover:shadow-lg transform hover:-translate-y-0.5' : ''}`}
                      style={{
                        backgroundColor: buttonColor,
                        color: buttonTextColor,
                        borderRadius
                      }}
                    >
                      {subscriptionStatus === 'loading' ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                          </svg>
                          Processing...
                        </>
                      ) : (
                        buttonText
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsletterSection;